import React, { useState, useEffect, useCallback } from "react";
import { Outlet, useNavigate, useParams, useLocation } from "react-router-dom";
import { AiOutlineShoppingCart } from "react-icons/ai";
import { useDispatch, useSelector } from "react-redux";
import { logoutAccount } from "../../services/logoutAccount";
import { toast } from "react-toastify";
import { getClientID } from "../../services/clientCode";
import { updateLinkTime } from "../../services/updateLinkTime";

const MainLayout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { client_code } = useParams();
  const cartItems = useSelector((state) => state.cart.cartItems);
  const [timeLeft, setTimeLeft] = useState(60);
  const [loading, setLoading] = useState(true);

  // Calculate if we should show checkout button (not on cart page)
  const showCheckoutButton =
    !location.pathname.includes("carts") && cartItems.length > 0;

  useEffect(() => {
    const handleBeforeUnload = async (event) => {
      const minutes = Math.floor(timeLeft / 60);
      const seconds = timeLeft % 60;
      const formattedTime = `${minutes}.${seconds.toString().padStart(2, "0")}`;
      await updateLinkTime({
        code: client_code,
        link_time: formattedTime,
      });
      event.preventDefault();
      event.returnValue = "";
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [client_code, timeLeft]);

  const handleLogout = useCallback(async () => {
    try {
      const minutes = Math.floor(timeLeft / 60);
      const seconds = timeLeft % 60;
      const formattedTime = `${minutes}.${seconds.toString().padStart(2, "0")}`;
      await updateLinkTime({
        code: client_code,
        link_time: formattedTime,
      });
      const response = await logoutAccount();
      if (response?.status) {
        localStorage.removeItem("token");
        toast.success(
          `Logged out with ${minutes}:${String(seconds).padStart(2, "0")} left`
        );
        navigate(`/${client_code}`);
      }
    } catch (error) {
      console.error("Error in handleLogout:", error);
    }
  }, [navigate, client_code, timeLeft]);

  const getClientData = async () => {
    try {
      const data = { code: client_code };
      setLoading(true);
      const response = await getClientID(data);
      if (response?.status) {
        const result = response?.data?.data;
        const isActive = result?.URL?.is_active === "Yes";
        const linkTimeMinutes = parseFloat(result?.URL?.link_time || 0);
        const linkTimeInSeconds = Math.round(linkTimeMinutes * 60);
        if (isActive || linkTimeInSeconds > 0) {
          setTimeLeft(linkTimeInSeconds);
        } else {
          toast.error("Your link has been expired!");
          navigate("/inactive");
        }
      }
    } catch (error) {
      console.log("Error fetching client data:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getClientData();
  }, []);

  useEffect(() => {
    if (timeLeft <= 0) {
      handleLogout();
      return;
    }

    const timerId = setInterval(() => {
      setTimeLeft((prev) => prev - 1);
    }, 1000);

    return () => clearInterval(timerId);
  }, [timeLeft, handleLogout]);

  const minutes = Math.floor(timeLeft / 60);
  const seconds = String(timeLeft % 60).padStart(2, "0");

  const handleCheckout = () => {
    navigate(`/app/${client_code}/carts`);
  };

  const totalWeight = cartItems
    .reduce((total, item) => {
      const itemWeight = parseFloat(item.weight || 0);
      const quantity = item.quantity || 0;
      return total + itemWeight * quantity;
    }, 0)
    .toFixed(2);

  return (
    <div className="min-h-screen flex flex-col relative">
      {/* Fixed Header */}
      <header className="bg-[#F7F6F1] text-dark sticky top-0 z-50 shadow-sm">
        <div className="p-4 flex justify-between items-center border-b border-[#dfdfdf]">
          <h3
            className="text-lg font-bold cursor-pointer"
            onClick={() => navigate(`/app/${client_code}`)}
          >
            Maina Payal
          </h3>
          <div className="flex gap-2 items-center">
            <button
              onClick={() => navigate("carts")}
              className="relative p-2"
              aria-label="Cart"
            >
              {cartItems?.length > 0 && (
                <span className="absolute -top-2 -right-2 bg-white text-[10px] px-2 py-1 rounded-full border">
                  {cartItems.length}
                </span>
              )}
              <AiOutlineShoppingCart size={25} />
            </button>
            <button
              onClick={handleLogout}
              className="bg-[#AD826D] px-3 py-1 text-sm text-white rounded-3xl hover:bg-[#9c7260] transition-colors"
            >
              Logout
            </button>
            <button
              onClick={() => navigate("orderhistory")}
              className="bg-[#AD826D] px-3 py-1 text-sm text-white rounded-3xl hover:bg-[#9c7260] transition-colors"
            >
              History
            </button>
          </div>
        </div>

        {/* Session Timer */}
        <div className="flex justify-between p-3 bg-white">
          <div className="text-center">
            {loading ? (
              <div className="text-gray-600">Loading...</div>
            ) : (
              <p className="text-gray-600">
                Session logout in <br></br>{" "}
                <span className="font-mono">
                  {minutes}:{seconds}
                </span>
              </p>
            )}
          </div>
          <div className="text-center">
            <p className="text-gray-600">
              Total Weight <br></br>{" "}
              <span className="font-mono">{totalWeight} gm</span>
            </p>
          </div>

          <div className="text-center">
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="inline-flex items-center border border-indigo-300 px-2 py-1 rounded-md bg-[#AD826D] text-white hover:bg-white-50"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 20 20"
                stroke="currentColor"
                className="h-4 w-4"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M7 16l-4-4m0 0l4-4m-4 4h18"
                ></path>
              </svg>
              <span className="ml-1 font-bold text-sm">Back</span>
            </button>
          </div>
        </div>
      </header>

      {/* Main Content with proper spacing */}
      <main className="flex-1 pb-4 pt-4 px-4 mb-16 bg-white overflow-y-auto">
        <Outlet />
      </main>

      {/* Absolutely positioned Checkout Button (only shown when not on cart page) */}
      {showCheckoutButton && (
        <div className="fixed bottom-14 left-0 right-0 bg-none mt-4 border-none border-gray-200 py-3 px-4 z-40">
          <button
            onClick={handleCheckout}
            className="w-full bg-[#AD826D] hover:bg-[#9c7260] text-white font-medium py-3 px-4 rounded-lg transition-colors duration-200"
          >
            Proceed to Checkout ({cartItems.length} items)
          </button>
        </div>
      )}

      {/* Regular Footer (not fixed) */}
      <footer className="bg-[#F7F6F1] text-dark text-center p-3 text-sm border-t border-gray-200">
        © 2025 Maina Payal. All rights reserved. Made with ❤️ by{" "}
        <a
          href="https://geekpeek.in/"
          target="_blank"
          rel="noopener noreferrer"
          className="text-[#AD826D] hover:underline"
        >
          Geekpeek Software and Technology
        </a>
      </footer>
    </div>
  );
};

export default MainLayout;
